/* eslint-disable */
/// <reference types="filesystem" />

window.requestFileSystem = window.requestFileSystem || window.webkitRequestFileSystem;

let storageType = window.TEMPORARY,
  storageSize = 1024 * 1024 * 1024 * 100; // TODO: simdilik dursun

function isLocalStorageEnabled() {
  try {
    var mod = '__storage_test__';
    localStorage.setItem(mod, mod);
    localStorage.removeItem(mod);
    return true;
  } catch (e) {
    return false;
  }
}

export function isSupported(): boolean {
  return typeof window.requestFileSystem !== 'undefined' && isLocalStorageEnabled();
}

/**
 * Queries temporary storage usage and quota.
 * @return {Promise<number[]>} Promise to used and granted byte lengths.
 */
export function queryTemporaryUsageAndQuota(): Promise<number[]> {
  return new Promise(function (resolve, reject) {
    const nav: any = navigator;
    nav.webkitTemporaryStorage.queryUsageAndQuota(
      function (usedBytes: any, grantedBytes: any) {
        resolve([usedBytes, grantedBytes]);
      },
      function (err: any) {
        reject(err);
      },
    );
  });
}

/**
 * Queries persistent storage usage and quota.
 * @return {Promise<number[]>} Promise to used and granted byte lengths.
 */
export function queryPersistentUsageAndQuota(): Promise<number[]> {
  return new Promise(function (resolve, reject) {
    const nav: any = navigator;
    nav.webkitPersistentStorage.queryUsageAndQuota(
      function (usedBytes: any, grantedBytes: any) {
        resolve([usedBytes, grantedBytes]);
      },
      function (err: any) {
        reject(err);
      },
    );
  });
}

/**
 * Requests persistent storage quota.
 * @param {number} requestedBytes Requested byte length.
 * @return {Promise<number>} Promise to granted byte length.
 */
export function requestPersistentQuota(requestedBytes: number = 1024 * 1024 * 10): Promise<number> {
  // 10MB
  return new Promise(function (resolve, reject) {
    const nav: any = navigator;
    nav.webkitPersistentStorage.requestQuota(
      requestedBytes,
      function (grantedBytes: any) {
        resolve(grantedBytes);
      },
      function (err: any) {
        reject(err);
      },
    );
  });
}

/**
 * Returns FileEntry object of file.
 * @param {string} path Path of file.
 * @return {Promise<FileEntry>} Promise to file entry.
 */
export function getFile(path: string): Promise<FileEntry> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        fs.root.getFile(
          path,
          {},
          function (fileEntry) {
            resolve(fileEntry);
          },
          errorHandler,
        );
      },
      errorHandler,
    );
  });
}

/**
 * Creates file.
 * If data is not provided, it just creates empty file.
 * @param {string} path Path of file to be created.
 * @param {Blob} data Data to be written.
 * @return {Promise<FileEntry>} Promise to FileEntry.
 */
export function createFile(path: string, data?: Blob): Promise<FileEntry> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        fs.root.getFile(
          path,
          { create: true, exclusive: true },
          function (fileEntry) {
            // if data exists write it, or just resolve
            if (typeof data !== 'undefined') {
              fileEntry.createWriter(function (fileWriter) {
                fileWriter.onwriteend = function () {
                  resolve(fileEntry);
                };
                fileWriter.onerror = errorHandler;
                fileWriter.write(data);
              }, errorHandler);
            } else {
              resolve(fileEntry);
            }
          },
          errorHandler,
        );
      },
      errorHandler,
    );
  });
}

/**
 * Creates dummy file.
 * It creates dummy file with respect to the size, all bytes of the file are filled with zero byte.
 * @param {string} path Path of file to be created.
 * @param {number} size File size in bytes.
 * @return {Promise<FileEntry>} Promise to FileEntry.
 */
export function createDummyFile(path: string, size: number): Promise<FileEntry> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        fs.root.getFile(
          path,
          { create: true, exclusive: false },
          function () {
            // create
            fs.root.getFile(
              path,
              { create: false },
              function (fileEntry) {
                // resize
                fileEntry.createWriter(function (fileWriter) {
                  fileWriter.onwriteend = function () {
                    resolve(fileEntry);
                  };
                  fileWriter.onerror = errorHandler;
                  fileWriter.truncate(size);
                }, errorHandler);
              },
              errorHandler,
            );
          },
          errorHandler,
        );
      },
      errorHandler,
    );
  });
}

/**
 * Reads content of the file as ArrayBuffer.
 * If start and end parameters is not provided, it reads whole file data.
 * @param {string} path Path of file to be read.
 * @param {number} start Start position of write operation.
 * @param {number} end End position of write operation.
 * @return {Promise<ArrayBuffer>} Promise to file content.
 */
export function readFile(path: string, start?: number, end?: number): Promise<ArrayBuffer> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        fs.root.getFile(
          path,
          {},
          function (fileEntry) {
            fileEntry.file(function (file) {
              let reader = new FileReader();
              reader.onloadend = function (ev: ProgressEvent<FileReader>) {
                resolve(ev.target?.result as ArrayBuffer);
              };

              if (typeof start !== 'number' || typeof end !== 'number') {
                reader.readAsArrayBuffer(file);
              } else {
                let blob = file.slice(start, end);
                reader.readAsArrayBuffer(blob);
              }
            }, errorHandler);
          },
          errorHandler,
        );
      },
      errorHandler,
    );
  });
}

/**
 * Writes data to the file.
 * If the file has content already, overwrite may occur.
 * @param {string} path Path of file to be written to.
 * @param {BufferSource} data Data to be written.
 * @param {number} start Start position of write operation, default is beginning of the file.
 * @return {Promise<string>} Promise to "Write completed." message.
 */
export function writeToFile(path: string, data: BufferSource, start: number = 0): Promise<string> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        fs.root.getFile(
          path,
          { create: true },
          function (fileEntry) {
            fileEntry.createWriter(function (fileWriter) {
              fileWriter.onwriteend = function () {
                resolve('Write completed.');
              };
              fileWriter.onerror = errorHandler;
              fileWriter.seek(start);
              fileWriter.write(new Blob([data]));
            }, errorHandler);
          },
          errorHandler,
        );
      },
      errorHandler,
    );
  });
}

/**
 * Appends given data to <b>end</b> of the file.
 * @param {string} path Path of file to be appended to.
 * @param {BufferSource} data Data to be appended.
 * @return {Promise<string>} Promise to "Append completed." message.
 */
export function appendToFile(path: string, data: BufferSource): Promise<string> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        fs.root.getFile(
          path,
          { create: false },
          function (fileEntry) {
            fileEntry.createWriter(function (fileWriter) {
              fileWriter.onwriteend = function () {
                resolve('Append completed.');
              };
              fileWriter.onerror = errorHandler;
              // Start write position at EOF.
              fileWriter.seek(fileWriter.length);
              fileWriter.write(new Blob([data]));
            }, errorHandler);
          },
          errorHandler,
        );
      },
      errorHandler,
    );
  });
}

/**
 * Removes file.
 * @param {string} path Path of file to be removed.
 * @return {Promise<string>} Promise to "File removed." message.
 */
export function removeFile(path: string): Promise<string> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        fs.root.getFile(
          path,
          { create: false },
          function (fileEntry) {
            fileEntry.remove(function () {
              resolve('File removed.');
            }, errorHandler);
          },
          errorHandler,
        );
      },
      errorHandler,
    );
  });
}

/**
 * Returns list of files and directories.
 * If path is not given, it lists root directory.
 * @return {Promise<Entry[]>} Promise to Entry list.
 */
export function listFiles(path: string = ''): Promise<Entry[]> {
  return new Promise(function (resolve, reject) {
    function errorHandler(err: any) {
      reject(err);
    }
    window.requestFileSystem(
      storageType,
      storageSize,
      function (fs) {
        let dirReader = fs.root.createReader();
        let entries: any = [];
        // Call the reader.readEntries() until no more results are returned.
        let readEntries = function () {
          dirReader.readEntries(function (results) {
            if (!results.length) {
              resolve(entries);
            } else {
              entries = entries.concat(results);
              readEntries();
            }
          }, errorHandler);
        };
        readEntries(); // Start reading dirs.
      },
      errorHandler,
    );
  });
}
